import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, money } from "@/lib/api";
import { toast } from "sonner";
import { ShoppingCart, X, QrCode, Smartphone, PackageCheck, Ban, RotateCcw, FileSpreadsheet } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "—");
const STATUSES = ["", "AWAITING_PAYMENT", "READY", "PICKED_UP", "EXPIRED", "CANCELLED", "REFUNDED"];
const BADGE = {
  AWAITING_PAYMENT: "bg-amber-100 text-amber-700", READY: "bg-green-100 text-green-700",
  PICKED_UP: "bg-slate-200 text-slate-600", EXPIRED: "bg-red-100 text-red-700",
  CANCELLED: "bg-red-100 text-red-700", REFUNDED: "bg-purple-100 text-purple-700",
};

export const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const reqId = useRef(0);

  const load = useCallback(async () => {
    const myReq = ++reqId.current;
    setLoading(true);
    try {
      const r = await api.get("/pos/orders", { params: { status: status || undefined, q: q || undefined } });
      if (myReq === reqId.current) setOrders(r.data.items);  // ignore stale responses
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  }, [status, q]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, path, body) => {
    try { await api.post(`/pos/orders/${id}/${path}`, body || {}); toast.success("Done"); load(); setSel(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const reopen = async (id) => {
    try {
      const r = await api.post(`/pos/orders/${id}/reopen`, { origin_url: window.location.origin });
      toast.success("Order reopened — new pay link ready");
      setSel(r.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reopen order"); }
  };

  const exportExcel = async () => {
    try {
      const r = await api.get("/pos/orders/export.xlsx", { params: { status: status || undefined, q: q || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "dealswagon-orders.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Orders Excel downloaded");
    } catch { toast.error("Could not export orders"); }
  };

  return (
    <div className="space-y-4" data-testid="orders-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-heading text-2xl text-brand-navy">Orders</div>
        <div className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order / item / email" data-testid="orders-search" className="h-9 border rounded-lg px-3 text-sm w-56" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="orders-status-filter" className="h-9 border rounded-lg px-2 text-sm">
            {STATUSES.map((s) => <option key={s} value={s}>{s ? s.replace(/_/g, " ") : "All statuses"}</option>)}
          </select>
          <button onClick={exportExcel} data-testid="orders-export-excel-btn" className="h-9 px-3 rounded-lg bg-brand-navy text-white font-bold text-sm inline-flex items-center gap-1"><FileSpreadsheet className="h-4 w-4" /> Download Excel</button>
        </div>
      </div>
      {loading ? <div className="text-slate-400">Loading…</div> : orders.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center" data-testid="orders-empty">
          <ShoppingCart className="h-10 w-10 mx-auto text-slate-300" />
          <div className="font-heading text-xl text-brand-navy mt-3">No orders</div>
          <p className="text-slate-500 mt-1">Create a pay link from Prepare Order to see orders here.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="p-3">Order #</th><th className="p-3">Items</th><th className="p-3">Total</th><th className="p-3">Status</th><th className="p-3">Created</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_id} data-testid={`order-row-${o.order_id}`} className="border-t border-slate-100">
                  <td className="p-3 font-bold">{o.invoice_number || o.order_id.slice(-8).toUpperCase()}</td>
                  <td className="p-3 text-slate-600">{o.items.reduce((s, l) => s + l.quantity, 0)} item(s)</td>
                  <td className="p-3">{money(o.total)}</td>
                  <td className="p-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${BADGE[o.status] || "bg-slate-100"}`}>{o.status.replace(/_/g, " ")}</span></td>
                  <td className="p-3 text-slate-500">{fmt(o.created_at)}</td>
                  <td className="p-3"><button onClick={() => setSel(o)} data-testid={`order-open-${o.order_id}`} className="text-brand-navy font-bold">Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sel && <OrderDetail order={sel} onClose={() => setSel(null)} onAct={act} onReopen={reopen} />}
    </div>
  );
};

const OrderDetail = ({ order, onClose, onAct, onReopen }) => {
  const awaiting = order.status === "AWAITING_PAYMENT";
  const paid = ["READY", "PAID"].includes(order.status);
  const reopenable = ["EXPIRED", "CANCELLED"].includes(order.status) && order.order_kind !== "HOLD";
  const [email, setEmail] = useState(order.customer_email || "");
  const [sending, setSending] = useState(false);
  const sendLink = async () => {
    if (!email.trim()) { toast.error("Enter a customer email"); return; }
    setSending(true);
    try { await api.post(`/pos/orders/${order.order_id}/send-link`, { email: email.trim() }); toast.success(`Pay link emailed to ${email.trim()}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send email"); }
    setSending(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="order-detail">
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white">
          <h2 className="font-heading text-xl text-brand-navy">Order {order.invoice_number || order.order_id.slice(-8).toUpperCase()}</h2>
          <button onClick={onClose} data-testid="order-detail-close"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-sm text-slate-500">{order.status.replace(/_/g, " ")} · {money(order.total)} {order.customer_email ? `· ${order.customer_email}` : ""}</div>
          <div className="divide-y border rounded-lg">
            {order.items.map((l) => (
              <div key={l.line_id} className="flex justify-between p-2 text-sm">
                <span>{l.quantity}× {l.product_name} <span className="text-slate-400">({l.sku})</span></span>
                <span className="font-bold">{money(l.line_total)}</span>
              </div>
            ))}
          </div>
          {order.pay_url && awaiting && (
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              {order.qr_data_url && <img src={order.qr_data_url} alt="QR" className="h-40 w-40 mx-auto" data-testid="order-qr" />}
              <div className="flex items-center gap-2 mt-2">
                <input readOnly value={order.pay_url} className="flex-1 bg-white border rounded px-2 h-9 text-xs" data-testid="order-pay-url" />
                <button onClick={() => { navigator.clipboard?.writeText(order.pay_url); toast.success("Copied"); }} className="h-9 px-3 rounded bg-brand-navy text-white text-sm font-bold">Copy</button>
              </div>
            </div>
          )}
          {order.pay_url && awaiting && (
            <div className="flex items-center gap-2">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Customer email" data-testid="order-email" className="flex-1 h-10 border border-slate-200 rounded-lg px-3 text-sm" />
              <button onClick={sendLink} disabled={sending} data-testid="order-send-link" className="h-10 px-4 rounded-lg bg-brand-navy text-white font-bold text-sm disabled:opacity-60">{sending ? "…" : "Email link"}</button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {awaiting && <button onClick={() => onAct(order.order_id, "mark-etransfer-paid")} data-testid="order-mark-paid" className="h-11 rounded-lg bg-brand-green text-white font-bold inline-flex items-center justify-center gap-2"><Smartphone className="h-4 w-4" /> Mark e-Transfer paid</button>}
            {awaiting && <button onClick={() => onAct(order.order_id, "cancel")} data-testid="order-cancel" className="h-11 rounded-lg border border-brand-red text-brand-red font-bold inline-flex items-center justify-center gap-2"><Ban className="h-4 w-4" /> Cancel</button>}
            {paid && <button onClick={() => onAct(order.order_id, "mark-picked-up")} data-testid="order-pickup" className="h-11 rounded-lg bg-brand-navy text-white font-bold inline-flex items-center justify-center gap-2"><PackageCheck className="h-4 w-4" /> Mark picked up</button>}
            {reopenable && <button onClick={() => onReopen(order.order_id)} data-testid="order-reopen" className="h-11 rounded-lg bg-brand-navy text-white font-bold inline-flex items-center justify-center gap-2"><RotateCcw className="h-4 w-4" /> Reopen order</button>}
            {order.payment_status === "paid" && order.status !== "REFUNDED" && (
              <button onClick={() => onAct(order.order_id, "refund", { manual: order.paid_method !== "Card" })} data-testid="order-refund" className="h-11 rounded-lg border border-purple-500 text-purple-700 font-bold inline-flex items-center justify-center gap-2"><RotateCcw className="h-4 w-4" /> Refund full</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
